import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { IShardRepository, StorageDataType } from "../../../api/entities/shard-repository";
import { shardRepositoryService } from "../../../api/services/shard-repository-service";
import { DefaultRouteAction, DefaultRouteParam } from "../../../constants/routes";
import { catchException, commonActions } from "../../../redux/actions/common";
import { IAppState } from "../../../redux/reducers";
import { IShardClusterRepository } from "../../shard-clusters/sections/storage-nodes-section/shard-repositories-section";
import { ShardRepositoryPanel, ShardRepositoryPanelMode } from "./shard-repository-panel/shard-repository-panel";
import { useApi } from "../../../utils/hooks/use-api";
import { tooltipUserGuideActions } from "../../../redux/actions/tooltip-user-guide";
import { CONFIG } from "../../../config/config";
import { accountService } from "../../../api/services/account-service";
import { routeUtils } from "../../../utils/route-utils";

export interface IShardRepositoryPanelContainerProps extends
    ReturnType<typeof mapDispatchToProps> {

    fetchStorageNodes: () => Promise<number>;
}


function ShardRepositoryContainedPanel(props: IShardRepositoryPanelContainerProps) {
    const [api] = useApi();

    const navigate = useNavigate();
    const { idOrAction } = useParams<{[DefaultRouteParam.IdOrAction]: string}>();
    const [poolNode, setPoolNode] = useState<IShardClusterRepository>(null);

    const [shardRepositoryPanelMode, setShardRepositoryPanelMode] = useState<ShardRepositoryPanelMode>(null);

    function handleEditShardRepository() {
        setShardRepositoryPanelMode(ShardRepositoryPanelMode.LimitedEdit);
    }

    const handleAddShardRepository = () => setShardRepositoryPanelMode(ShardRepositoryPanelMode.Add);
    const handleCloseShardRepositoryPanel = () => {
        setPoolNode(null);
        setShardRepositoryPanelMode(null);

        navigate(routeUtils.getRouteToShardRepositoryList());
    }

    useEffect(() => {
        setPoolNode(null);
        if (idOrAction === DefaultRouteAction.New) {
            handleAddShardRepository();
        }
        else if (!!idOrAction) {
            handleEditShardRepository();

            const getStorageNodeType = (storageNode: IShardRepository, type: StorageDataType) => storageNode.types?.includes(type) ?? false;

            props.startLoading();

            api(shardRepositoryService.get(idOrAction))
                .then((shardRepository) => {
                    setPoolNode({
                        shardRepository: shardRepository,
                        types: {
                            storeContent: getStorageNodeType(shardRepository, StorageDataType.Content),
                            storeKey: getStorageNodeType(shardRepository, StorageDataType.Key),
                            storeMetadata: getStorageNodeType(shardRepository, StorageDataType.Meta),
                        }
                    });
                })
                .catch(catchException())
                .finally(props.stopLoading);
        }
        // eslint-disable-next-line
    }, [idOrAction]);

    function handleStorageNodeSaved() {
        props.fetchStorageNodes()
            .then(nodeCount => {
                if (nodeCount >= CONFIG.minStoragesCount
                    && accountService.getTooltipUserGuideState().showTooltip) {
                        props.shardRepositoryTooltipOpen();
                }
            });
        handleCloseShardRepositoryPanel();
    }

    return (
        <ShardRepositoryPanel
            mode={shardRepositoryPanelMode}
            shardClusterRepository={poolNode}
            onClose={handleCloseShardRepositoryPanel}
            onRepositorySaved={handleStorageNodeSaved}
        />
    );
}

function mapDispatchToProps(dispatch: ThunkDispatch<IAppState, void, Action>) {
    return {
        startLoading: () => dispatch(commonActions.startLoading()),
        stopLoading: () => dispatch(commonActions.stopLoading()),

        shardRepositoryTooltipOpen: () => {
            dispatch(tooltipUserGuideActions.disableStepCounter());
            dispatch(tooltipUserGuideActions.open());
            dispatch(tooltipUserGuideActions.setStep("shardCluster"));
        },
    };
}

export default connect(
    null,
    mapDispatchToProps
)(ShardRepositoryContainedPanel);
